import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { RECENT_POS } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import FilterDropdown from '../../components/FilterDropdown';
import { ACTIVE, IN_ACTIVE, SUNSET, PLANNED, PHASED_STATUSES, enumInventoryPhasedStatus } from '../../consts/inventoryStatus';

function formatCurrency(value) {
  const n = Number(value);
  if (value === null || value === undefined || value === '') return '-';
  if (Number.isNaN(n)) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function getProductCatalogUrl(sku) {
  const params = new URLSearchParams({
    viewTable: 'default',
    search: sku,
    page: '1',
    itemsPerPage: '50',
    statuses: '',
    categories: '',
    subcategories: '',
    brandPillars: '',
    tiers: '',
    entryEngines: '',
    intents: '',
    containerGroups: '',
    products: '',
  });
  return `${ROUTES.PRODUCTS_CATALOG}?${params.toString()}`;
}

const RecentPos = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;
  const [skuSearch, setSkuSearch] = useState('');
  const [debouncedSkuSearch, setDebouncedSkuSearch] = useState('');
  const [selectedPhasedStatuses, setSelectedPhasedStatuses] = useState([ACTIVE, IN_ACTIVE, SUNSET, PLANNED]);

  const columns = useMemo(
    () => [
      { key: 'sku', label: 'SKU', type: 'string', sortable: true, sticky: true },
      {
        key: 'unit_cost',
        label: 'Unit Cost',
        type: 'currency',
        sortable: true,
        align: 'right',
      },
      {
        key: 'most_recent_po_unit_cost',
        label: 'Most Recent PO Unit Cost',
        type: 'currency',
        sortable: true,
        align: 'right',
      },
      {
        key: 'previous_po_unit_cost',
        label: 'Previous PO Unit Cost',
        type: 'currency',
        sortable: true,
        align: 'right',
      },
      { key: 'status', label: 'Status', type: 'string', sortable: true },
    ],
    []
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSkuSearch(skuSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [skuSearch]);

  const fetchData = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(pageSize),
      });
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      if (debouncedSkuSearch) {
        params.append('sku', debouncedSkuSearch);
      }
      if (selectedPhasedStatuses && selectedPhasedStatuses.length > 0) {
        params.append('phasedStatuses', selectedPhasedStatuses.join(','));
      }

      const res = await fetch(`/api/v1/reports/${RECENT_POS}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setTotalPages(json.totalPages || 1);
      setTotalRecords(json.totalRecords || 0);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [currentPage, pageSize, sortField, sortDirection, debouncedSkuSearch, selectedPhasedStatuses, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const renderRows = () =>
    (data || []).map((row, idx) => (
      <tr key={`${row.sku || 'sku'}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row.sku ? (
            <a
              href={getProductCatalogUrl(row.sku)}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {row.sku}
            </a>
          ) : (
            ''
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {formatCurrency(row.unit_cost)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {formatCurrency(row.most_recent_po_unit_cost)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {formatCurrency(row.previous_po_unit_cost)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row.status || ''}
        </td>
      </tr>
    ));

  const filtersComponent = (
    <div className="contents">
      <input
        type="text"
        value={skuSearch}
        onChange={(e) => {
          setSkuSearch(e.target.value);
          setCurrentPage(1);
        }}
        placeholder="Search SKU"
        className="w-40 sm:w-[200px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
      <div className="relative">
        <FilterDropdown
          options={PHASED_STATUSES}
          selectedOptions={selectedPhasedStatuses}
          onChange={(value) => {
            setSelectedPhasedStatuses(value);
            setCurrentPage(1);
          }}
          placeholder="Product Status"
          renderOption={(phase) => (
            <span className="text-gray-900">{enumInventoryPhasedStatus[phase]}</span>
          )}
          disableScrollRecalculation={true}
        />
      </div>
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={columns}
      filename={RECENT_POS}
      onExport={async () => {
        const params = new URLSearchParams({});
        if (sortField) {
          params.append('sortField', sortField);
          params.append('sortDirection', sortDirection);
        }
        if (debouncedSkuSearch) {
          params.append('sku', debouncedSkuSearch);
        }
        if (selectedPhasedStatuses && selectedPhasedStatuses.length > 0) {
          params.append('phasedStatuses', selectedPhasedStatuses.join(','));
        }
        const res = await fetch(`/api/v1/reports/${RECENT_POS}/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const exportFilename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${RECENT_POS}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = exportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
      disabled={isLoading}
      variant="outline"
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalRecords)} of{' '}
          {totalRecords} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  return (
    <ReportTemplate
      reportId={RECENT_POS}
      description="Most recent and previous PO unit costs by SKU (OPEN and CLOSED)"
      breadcrumbs={breadcrumbs}
      onBack={handleBackToReports}
      marginFromSidebar={marginFromSidebar}
      isLoading={isLoading}
      error={error}
      filters={filtersComponent}
      exportButton={exportButton}
      data={data}
      columns={columns}
      pagination={paginationComponent}
      emptyMessage="No products found."
      onSort={handleSort}
      sortField={sortField}
      sortDirection={sortDirection}
      enableSorting={true}
    >
      {renderRows()}
    </ReportTemplate>
  );
};

export default RecentPos;
